import { type Dictionary } from '@ngrx/entity';

import {
  applyPropsPatch,
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementType,
  type SlotId,
  type StyleConfigProps,
} from '@blaze-runtime/engine-domain';
import {
  ACTION_ID_TO_PROPS_PATH_MAPPING,
  type ItemContainerActionsIds,
  STYLE_CONFIG_PROPERTIES_PATH,
  type StyleConfig,
} from '@blaze-shared/elements-domain';
import { type StyleSheet } from '@blaze-shared/theming-domain';

import {
  type CanvasElementDefinition,
  type CanvasElementDefinitionInternal,
} from '../../canvas-elements/element-definition';
import { hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';

import { applyMissingInitialProps } from './style-initials';
import { cleanStylePaths, cleanStylePathsFromPropsOverrides } from './style-path-cleanup';
import { getDefaultStyleSheet } from './style-sheet-resolution';
import { hasStyleableSlots } from './style-slot-target';

export interface SetElementStyleData {
  entities: Dictionary<CanvasElement>;
  elementId: CanvasElementId;
  slotId?: SlotId;
  styleSheet: StyleSheet;
  elementDefinition: CanvasElementDefinition | CanvasElementDefinitionInternal;
}

export interface SetStyleSheetForActionData {
  entities: Dictionary<CanvasElement>;
  elementId: CanvasElementId;
  actionId: ItemContainerActionsIds;
  styleSheetId?: string | null;
  styleSheetName?: string | null;
}

function makeStyleConfigForStyleSheet(styleSheet: StyleSheet): StyleConfig {
  const isLocal = styleSheet.themeId !== undefined;

  if (isLocal) {
    return {
      styleName: styleSheet.name,
    };
  }

  return {
    styleSheetId: styleSheet.id,
    lastUsedStyleSheetId: styleSheet.id,
  };
}

export function makeStyleConfigPropsForDefaultStyleSheet(
  styleSheets: StyleSheet[],
  elementType: CanvasElementType,
): { [STYLE_CONFIG_PROPERTIES_PATH]: StyleConfig } | undefined {
  const defaultStyleSheet = getDefaultStyleSheet(elementType, styleSheets);

  if (!defaultStyleSheet) {
    return;
  }

  return {
    [STYLE_CONFIG_PROPERTIES_PATH]: makeStyleConfigForStyleSheet(defaultStyleSheet),
  };
}

/**
 * Sets style to the element.
 * - Cleans style data based on definition from props and overrides.
 * - Applies missing initials (Element only).
 * - Sets style configuration.
 * @param data
 */
export function setStyleSheetToElementMutably(data: SetElementStyleData): void {
  const { entities, elementDefinition, elementId, slotId, styleSheet } = data;

  const element = entities[elementId];
  if (!hasProps(element)) {
    throw new Error('Element does not have props');
  }

  const config = makeStyleConfigForStyleSheet(styleSheet);

  if (slotId && hasStyleableSlots(element)) {
    const slot = element.slots[slotId];

    if (slot) {
      slot.props = cleanStylePaths(slot.props, elementDefinition);
      slot.overrides = cleanStylePathsFromPropsOverrides(slot.overrides, elementDefinition);
      (slot.props as StyleConfigProps)[STYLE_CONFIG_PROPERTIES_PATH] = config;
    }

    return;
  }

  element.props = cleanStylePaths(element.props, elementDefinition);
  element.overrides = cleanStylePathsFromPropsOverrides(element.overrides, elementDefinition);
  element.props = applyMissingInitialProps(element.props, elementDefinition, styleSheet);
  (element.props as StyleConfigProps)[STYLE_CONFIG_PROPERTIES_PATH] = config;
}

export function setStyleSheetForActionMutably(data: SetStyleSheetForActionData): void {
  const { entities, elementId, actionId, styleSheetId, styleSheetName } = data;

  const element = entities[elementId];
  if (
    !(
      isElementType(element, 'table') ||
      isElementType(element, 'list') ||
      isElementType(element, 'kanbanBoard') ||
      isElementType(element, 'ganttChart')
    )
  ) {
    return;
  }
  const key = ACTION_ID_TO_PROPS_PATH_MAPPING[actionId];
  if (!key) {
    return;
  }

  if (styleSheetId && styleSheetName === undefined) {
    element.props = applyPropsPatch(element.props, {
      content: { [key]: { styleSheetTarget: { id: styleSheetId, name: undefined } } },
    }) as any;
    return;
  }

  if (styleSheetName && styleSheetId === undefined) {
    element.props = applyPropsPatch(element.props, {
      content: { [key]: { styleSheetTarget: { id: undefined, name: styleSheetName } } },
    }) as any;
    return;
  }

  delete (element.props.content as any)[key].styleSheetTarget;

  // Remove deprecated field
  delete (element.props.content as any)[key].styleSheetId;
}
